// src/utils/textExtract.js
// -----------------------------------------------------------------------------
// Pulls plain text out of uploaded .pdf, .docx, and .txt files, entirely in the
// browser — no Cloud Function round trip, no upload size ceiling.
//
// SETUP
//   npm install mammoth pdfjs-dist@3.11.174
//
// pdfjs-dist v4+ ships ESM with top-level await, which CRA's webpack cannot
// build. Pin to 3.11.174 and use the legacy bundle, as below.
//
// Then copy the worker into the public folder so it is served from your own
// origin (CRA cannot dynamic-import it from a CDN):
//
//   cp node_modules/pdfjs-dist/legacy/build/pdf.worker.min.js public/pdf.worker.min.js
//
// Re-run that copy after any pdfjs-dist upgrade — a worker/library version
// mismatch throws at runtime, not at build time.
// -----------------------------------------------------------------------------

import mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// Below this many characters per page, we assume there is no text layer and the
// PDF is a scan.
const SCAN_THRESHOLD_CHARS_PER_PAGE = 60;

function readAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Rebuild paragraphs from PDF text items using vertical gaps between lines.
 * PDF has no concept of a paragraph, so without this everything arrives as one
 * undifferentiated block and the paragraph-for-paragraph guarantee falls apart.
 */
function itemsToParagraphs(items) {
  const lines = [];
  let currentLine = [];
  let lastY = null;
  let lastHeight = 12;

  items.forEach((item) => {
    const y = item.transform[5];
    const height = item.height || lastHeight;
    if (lastY !== null && Math.abs(y - lastY) > height * 0.5) {
      if (currentLine.length) lines.push({ text: currentLine.join('').trim(), gap: lastY - y });
      currentLine = [];
    }
    currentLine.push(item.str);
    if (item.hasEOL) currentLine.push(' ');
    lastY = y;
    lastHeight = height;
  });
  if (currentLine.length) lines.push({ text: currentLine.join('').trim(), gap: lastHeight });

  const realLines = lines.filter((l) => l.text);
  if (realLines.length === 0) return '';

  const gaps = realLines.map((l) => l.gap).filter((g) => g > 0).sort((a, b) => a - b);
  const medianGap = gaps.length ? gaps[Math.floor(gaps.length / 2)] : lastHeight;

  const paragraphs = [];
  let buffer = [];
  realLines.forEach((line, i) => {
    buffer.push(line.text);
    const next = realLines[i + 1];
    // A gap noticeably bigger than the typical line gap means a new paragraph.
    if (!next || next.gap > medianGap * 1.6) {
      paragraphs.push(buffer.join(' '));
      buffer = [];
    }
  });
  if (buffer.length) paragraphs.push(buffer.join(' '));

  return paragraphs.join('\n\n');
}

async function extractPdf(file) {
  const buffer = await readAsArrayBuffer(file);
  const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
  const pages = [];

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const content = await page.getTextContent();
    pages.push(itemsToParagraphs(content.items));
  }

  const text = pages.filter(Boolean).join('\n\n');
  const charsPerPage = text.length / Math.max(1, pdf.numPages);

  return {
    text,
    pages: pdf.numPages,
    likelyScanned: charsPerPage < SCAN_THRESHOLD_CHARS_PER_PAGE,
  };
}

async function extractDocx(file) {
  const buffer = await readAsArrayBuffer(file);
  const result = await mammoth.extractRawText({ arrayBuffer: buffer });
  return { text: (result.value || '').trim(), likelyScanned: false };
}

async function extractTxt(file) {
  const text = await file.text();
  return { text: text.trim(), likelyScanned: false };
}

/**
 * Normalise whitespace without destroying paragraph breaks, and strip the
 * hyphens PDFs insert at line ends.
 */
export function tidy(text) {
  return String(text || '')
    .replace(/\r\n?/g, '\n')
    .replace(/([a-z])-\s+([a-z])/g, '$1$2')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .split('\n\n')
    .map((p) => p.trim())
    .filter(Boolean)
    .join('\n\n')
    .trim();
}

/**
 * @param {File} file
 * @returns {Promise<{text: string, pages?: number, likelyScanned: boolean}>}
 */
export async function extractText(file) {
  const name = (file.name || '').toLowerCase();

  let result;
  if (name.endsWith('.pdf')) {
    result = await extractPdf(file);
  } else if (name.endsWith('.docx')) {
    result = await extractDocx(file);
  } else if (name.endsWith('.txt') || name.endsWith('.md')) {
    result = await extractTxt(file);
  } else if (name.endsWith('.doc')) {
    throw new Error('Old .doc files are not supported. Save it as .docx and upload again.');
  } else {
    throw new Error('Upload a PDF, a Word document (.docx), or a plain text file.');
  }

  return { ...result, text: tidy(result.text) };
}
